import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Slider,
  Stack,
  IconButton,
} from '@mui/material';
import { Close } from '@mui/icons-material';

const FieldStyleDialog = ({ open, onClose, onSave, fieldConfig }) => {
  const [config, setConfig] = useState({
    fontWeight: (fieldConfig || {}).fontWeight || 'normal',
    fontSize: (fieldConfig || {}).fontSize || 14,
    fontStyle: (fieldConfig || {}).fontStyle || 'normal',
    textDecoration: (fieldConfig || {}).textDecoration || 'none',
    color: (fieldConfig || {}).color || '#000000',
    backgroundColor: (fieldConfig || {}).backgroundColor || 'transparent',
    padding: (fieldConfig || {}).padding || 8,
    margin: (fieldConfig || {}).margin || 4,
    borderRadius: (fieldConfig || {}).borderRadius || 4,
    border: (fieldConfig || {}).border || 'none',
    textAlign: (fieldConfig || {}).textAlign || 'left',
    lineHeight: (fieldConfig || {}).lineHeight || 1.5,
    letterSpacing: (fieldConfig || {}).letterSpacing || 0,
  });

  const handleChange = (property, value) => {
    setConfig((prev) => ({
      ...prev,
      [property]: value,
    }));
  };

  const handleSave = () => {
    onSave(config);
    onClose();
  };

  const fontWeights = [
    { value: 'normal', label: 'Normal' },
    { value: 'bold', label: 'Negrito' },
    { value: 'lighter', label: 'Leve' },
    { value: '100', label: '100' },
    { value: '200', label: '200' },
    { value: '300', label: '300' },
    { value: '400', label: '400' },
    { value: '500', label: '500' },
    { value: '600', label: '600' },
    { value: '700', label: '700' },
    { value: '800', label: '800' },
    { value: '900', label: '900' },
  ];

  const fontStyles = [
    { value: 'normal', label: 'Normal' },
    { value: 'italic', label: 'Itálico' },
    { value: 'oblique', label: 'Obliquo' },
  ];

  const textDecorations = [
    { value: 'none', label: 'Nenhum' },
    { value: 'underline', label: 'Sublinhado' },
    { value: 'overline', label: 'Sobrelinhado' },
    { value: 'line-through', label: 'Riscado' },
  ];

  const textAligns = [
    { value: 'left', label: 'Esquerda' },
    { value: 'center', label: 'Centro' },
    { value: 'right', label: 'Direita' },
    { value: 'justify', label: 'Justificado' },
  ];

  const borders = [
    { value: 'none', label: 'Nenhuma' },
    { value: '1px solid #ccc', label: 'Fina simples' },
    { value: '2px solid #ccc', label: 'Média simples' },
    { value: '1px dashed #ccc', label: 'Tracejada' },
    { value: '1px dotted #ccc', label: 'Pontilhada' },
    { value: '2px solid #1976d2', label: 'Azul média' },
    { value: '1px solid #1976d2', label: 'Azul fina' },
  ];

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        Configurar Estilo do Campo
        <IconButton onClick={onClose} size="small">
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pb: 2 }}>
        <Stack spacing={3}>
          {/* Tipografia */}
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
              Tipografia
            </Typography>

            <Stack spacing={2}>
              <FormControl size="small" fullWidth>
                <InputLabel>Peso da Fonte</InputLabel>
                <Select
                  value={config.fontWeight}
                  label="Peso da Fonte"
                  onChange={(e) => handleChange('fontWeight', e.target.value)}
                >
                  {fontWeights.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <Box>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  Tamanho da Fonte: {config.fontSize}px
                </Typography>
                <Slider
                  value={config.fontSize}
                  onChange={(e, value) => handleChange('fontSize', value)}
                  min={8}
                  max={32}
                  step={1}
                  marks={[
                    { value: 8, label: '8px' },
                    { value: 12, label: '12px' },
                    { value: 14, label: '14px' },
                    { value: 16, label: '16px' },
                    { value: 18, label: '18px' },
                    { value: 24, label: '24px' },
                    { value: 32, label: '32px' },
                  ]}
                />
              </Box>

              <FormControl size="small" fullWidth>
                <InputLabel>Estilo da Fonte</InputLabel>
                <Select
                  value={config.fontStyle}
                  label="Estilo da Fonte"
                  onChange={(e) => handleChange('fontStyle', e.target.value)}
                >
                  {fontStyles.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl size="small" fullWidth>
                <InputLabel>Decoração do Texto</InputLabel>
                <Select
                  value={config.textDecoration}
                  label="Decoração do Texto"
                  onChange={(e) => handleChange('textDecoration', e.target.value)}
                >
                  {textDecorations.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <Box>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  Espaçamento entre letras: {config.letterSpacing}px
                </Typography>
                <Slider
                  value={config.letterSpacing}
                  onChange={(e, value) => handleChange('letterSpacing', value)}
                  min={-2}
                  max={5}
                  step={0.5}
                  marks={[
                    { value: -2, label: '-2px' },
                    { value: 0, label: '0px' },
                    { value: 2, label: '2px' },
                    { value: 5, label: '5px' },
                  ]}
                />
              </Box>

              <FormControl size="small" fullWidth>
                <InputLabel>Alinhamento</InputLabel>
                <Select
                  value={config.textAlign}
                  label="Alinhamento"
                  onChange={(e) => handleChange('textAlign', e.target.value)}
                >
                  {textAligns.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <Box>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  Altura da linha: {config.lineHeight}
                </Typography>
                <Slider
                  value={config.lineHeight}
                  onChange={(e, value) => handleChange('lineHeight', value)}
                  min={1}
                  max={3}
                  step={0.1}
                  marks={[
                    { value: 1, label: '1' },
                    { value: 1.2, label: '1.2' },
                    { value: 1.5, label: '1.5' },
                    { value: 2, label: '2' },
                    { value: 3, label: '3' },
                  ]}
                />
              </Box>
            </Stack>
          </Box>

          {/* Cores */}
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
              Cores
            </Typography>

            <Stack spacing={2}>
              <TextField
                label="Cor do Texto"
                type="color"
                value={config.color}
                onChange={(e) => handleChange('color', e.target.value)}
                size="small"
                fullWidth
              />

              <TextField
                label="Cor de Fundo"
                type="color"
                value={config.backgroundColor}
                onChange={(e) => handleChange('backgroundColor', e.target.value)}
                size="small"
                fullWidth
              />
            </Stack>
          </Box>

          {/* Espaçamento e Borda */}
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
              Espaçamento e Borda
            </Typography>

            <Stack spacing={2}>
              <Box>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  Padding: {config.padding}px
                </Typography>
                <Slider
                  value={config.padding}
                  onChange={(e, value) => handleChange('padding', value)}
                  min={0}
                  max={20}
                  step={1}
                  marks={[
                    { value: 0, label: '0px' },
                    { value: 4, label: '4px' },
                    { value: 8, label: '8px' },
                    { value: 12, label: '12px' },
                    { value: 16, label: '16px' },
                    { value: 20, label: '20px' },
                  ]}
                />
              </Box>

              <Box>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  Margin: {config.margin}px
                </Typography>
                <Slider
                  value={config.margin}
                  onChange={(e, value) => handleChange('margin', value)}
                  min={0}
                  max={20}
                  step={1}
                  marks={[
                    { value: 0, label: '0px' },
                    { value: 4, label: '4px' },
                    { value: 8, label: '8px' },
                    { value: 12, label: '12px' },
                    { value: 16, label: '16px' },
                    { value: 20, label: '20px' },
                  ]}
                />
              </Box>

              <Box>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  Raio da Borda: {config.borderRadius}px
                </Typography>
                <Slider
                  value={config.borderRadius}
                  onChange={(e, value) => handleChange('borderRadius', value)}
                  min={0}
                  max={20}
                  step={1}
                  marks={[
                    { value: 0, label: '0px' },
                    { value: 4, label: '4px' },
                    { value: 8, label: '8px' },
                    { value: 12, label: '12px' },
                    { value: 16, label: '16px' },
                    { value: 20, label: '20px' },
                  ]}
                />
              </Box>

              <FormControl size="small" fullWidth>
                <InputLabel>Borda</InputLabel>
                <Select
                  value={config.border}
                  label="Borda"
                  onChange={(e) => handleChange('border', e.target.value)}
                >
                  {borders.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Stack>
          </Box>

          {/* Preview */}
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
              Preview
            </Typography>
            <Box
              sx={{
                p: 2,
                border: '1px solid #ccc',
                borderRadius: 1,
                bgcolor: '#f9f9f9',
              }}
            >
              <Typography
                sx={{
                  fontWeight: config.fontWeight,
                  fontSize: `${config.fontSize}px`,
                  fontStyle: config.fontStyle,
                  textDecoration: config.textDecoration,
                  color: config.color,
                  backgroundColor: config.backgroundColor,
                  padding: `${config.padding}px`,
                  margin: `${config.margin}px`,
                  borderRadius: `${config.borderRadius}px`,
                  border: config.border,
                  textAlign: config.textAlign,
                  lineHeight: config.lineHeight,
                  letterSpacing: `${config.letterSpacing}px`,
                }}
              >
                Exemplo de Texto
              </Typography>
            </Box>
          </Box>
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose}>Cancelar</Button>
        <Button onClick={handleSave} variant="contained">
          Salvar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default FieldStyleDialog;
